export interface JobOpening {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  experience: string;
  summary: string;
  responsibilities: string[];
  requirements: string[];
}

export const JOB_OPENINGS: JobOpening[] = [
  {
    id: "operation-manager",
    title: "Operation Manager",
    department: "Operations",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "10+ Years",
    summary:
      "Lead and oversee daily operations across our interior fit-out and manufacturing divisions, ensuring efficient coordination between design, production, and site execution teams.",
    responsibilities: [
      "Oversee day-to-day operations across fit-out, joinery, and manufacturing workflows",
      "Coordinate between design, procurement, production, and site teams to maintain project schedules",
      "Implement and monitor operational processes, quality standards, and ISO compliance",
      "Manage resource allocation, capacity planning, and production scheduling",
      "Drive continuous improvement in efficiency, cost control, and output quality",
      "Report on operational KPIs and project progress to senior leadership",
    ],
    requirements: [
      "10+ years of operations management experience in interiors, construction, or manufacturing",
      "Strong leadership and cross-functional coordination skills",
      "Experience with ISO quality, environmental, and safety standards",
      "Excellent problem-solving and decision-making abilities",
    ],
  },
  {
    id: "project-manager",
    title: "Project Manager",
    department: "Project Management",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "8+ Years",
    summary:
      "Manage interior fit-out projects from award to handover, coordinating clients, consultants, subcontractors, and internal teams to deliver on time, on budget, and to specification.",
    responsibilities: [
      "Lead interior fit-out projects from planning through to final handover",
      "Prepare and manage project schedules, budgets, and procurement plans",
      "Coordinate with clients, consultants, MEP teams, and site supervisors",
      "Monitor site progress, quality control, and compliance with drawings and specifications",
      "Manage RFIs, variations, and client approvals throughout the project lifecycle",
      "Ensure all safety and quality standards are maintained on site",
    ],
    requirements: [
      "Civil Engineering Graduate",
      "8+ years of experience in the same field",
    ],
  },
  {
    id: "project-engineer",
    title: "Project Engineer",
    department: "Project Management",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "5+ Years",
    summary:
      "Support the coordination and execution of interior fit-out projects, handling technical supervision, drawing coordination, and on-site quality control alongside project managers and site teams.",
    responsibilities: [
      "Assist project managers in planning, scheduling, and monitoring interior fit-out projects",
      "Coordinate with site supervisors, subcontractors, and factory teams to maintain project progress",
      "Review and interpret drawings, specifications, and shop drawings for accuracy and constructability",
      "Monitor site quality control, material approvals, and compliance with project requirements",
      "Prepare progress reports, site documentation, and resolve technical queries and RFIs",
      "Ensure adherence to safety, quality, and ISO standards throughout project execution",
    ],
    requirements: [
      "Civil Engineering Graduate",
      "5+ years of experience in the same field",
    ],
  },
  {
    id: "electrical-engineer",
    title: "Electrical Engineer",
    department: "MEP Engineering",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "5+ Years",
    summary:
      "Design, coordinate, and supervise electrical and low-current systems across interior fit-out projects, including lighting, power, and smart building integrations.",
    responsibilities: [
      "Design and review electrical layouts, lighting plans, and power distribution for interior projects",
      "Coordinate low-current systems: CCTV, access control, fire alarm, and network cabling",
      "Prepare load calculations, single-line diagrams, and technical specifications",
      "Liaise with MEP consultants, contractors, and authorities for approvals",
      "Supervise installation and commissioning of electrical works on site",
      "Ensure compliance with Bahrain municipal and utility authority requirements",
    ],
    requirements: [
      "5+ years of electrical engineering experience in MEP or interior fit-out",
      "BSc in Electrical Engineering",
      "Proficiency in AutoCAD and electrical design software",
      "Knowledge of Bahrain electrical codes and authority approval processes",
    ],
  },
  {
    id: "interior-draftsman",
    title: "Interior Draftsman",
    department: "Design Studio",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "3+ Years",
    summary:
      "Prepare detailed shop drawings and technical documentation for interior fit-out and joinery projects, translating design concepts into accurate, fabrication-ready drawings.",
    responsibilities: [
      "Prepare detailed shop drawings for interior fit-out, joinery, and furniture",
      "Convert design concepts and sketches into accurate technical drawings",
      "Coordinate drawings with architectural, MEP, and structural references",
      "Produce setting-out drawings, detail drawings, and joinery production drawings",
      "Maintain drawing registers, revision control, and documentation standards",
      "Liaise with site teams and factory to resolve drawing-related queries",
    ],
    requirements: [
      "3+ years of experience preparing interior shop drawings",
      "Proficiency in AutoCAD (2D and 3D)",
      "Strong understanding of joinery detailing, materials, and construction methods",
      "Ability to read architectural and MEP drawings accurately",
    ],
  },
  {
    id: "3d-visualiser",
    title: "3D Visualizer with Drafting Knowledge",
    department: "Design Studio",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "5+ Years",
    summary:
      "Create photorealistic 3D renders and technical drawings of interior spaces, combining visualisation skills with drafting capability to support the design studio in presenting concepts to clients and stakeholders across the GCC.",
    responsibilities: [
      "Produce photorealistic 3D interior renders and walkthrough animations",
      "Prepare accurate AutoCAD shop drawings and technical details alongside 3D work",
      "Model interior spaces, furniture, and joinery in 3Ds Max",
      "Apply materials, lighting, and V-Ray rendering to achieve premium presentation quality",
      "Work closely with designers to interpret and visualise design intent",
      "Manage render pipelines, drawing standards, and maintain asset libraries",
    ],
    requirements: [
      "Graduate with knowledge in AutoCAD, 3Ds Max, and V-Ray",
      "5+ years of experience in the same field",
    ],
  },
  {
    id: "carpenter",
    title: "Carpenter",
    department: "Joinery & Manufacturing",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "3+ Years",
    summary:
      "Fabricate and install bespoke joinery, furniture, and architectural woodwork in our Askar manufacturing facility, working to precise drawings and premium finish standards.",
    responsibilities: [
      "Fabricate custom furniture, cabinets, doors, and architectural joinery",
      "Read and interpret shop drawings accurately",
      "Operate woodworking machinery and hand tools safely and precisely",
      "Assemble, install, and adjust joinery on site as required",
      "Maintain quality standards and material handling best practices",
      "Work with solid timber, veneers, MDF, plywood, and panel products",
    ],
    requirements: [
      "3+ years of carpentry experience in furniture or architectural joinery",
      "Ability to read shop drawings and work to tight tolerances",
      "Experience with both machine and hand carpentry techniques",
      "Attention to detail and pride in craftsmanship",
    ],
  },
  {
    id: "painter",
    title: "Painter",
    department: "Finishing & Spray",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "3+ Years",
    summary:
      "Apply premium paint, lacquer, and spray finishes to joinery and furniture in our dust-free spray facility, achieving flawless, consistent results to luxury standards.",
    responsibilities: [
      "Apply spray lacquer, PU, and paint finishes to furniture and joinery",
      "Prepare surfaces: sanding, priming, filling, and undercoating",
      "Operate spray equipment in dust-free booths to achieve smooth, even finishes",
      "Mix and match colours and finishes to specification",
      "Perform quality checks on finish consistency, coverage, and adhesion",
      "Maintain spray equipment and booth cleanliness",
    ],
    requirements: [
      "3+ years of experience in spray painting or furniture finishing",
      "Experience with PU, lacquer, and high-gloss finishes",
      "Ability to achieve consistent, blemish-free results",
      "Understanding of surface preparation and material compatibility",
    ],
  },
  {
    id: "polisher",
    title: "Polisher",
    department: "Finishing & Spray",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "3+ Years",
    summary:
      "Polish and refine finished surfaces to achieve premium gloss and texture, ensuring every piece of joinery meets the exacting standards expected by international luxury brands.",
    responsibilities: [
      "Polish and buff lacquered and painted surfaces to specified gloss levels",
      "Perform rubbing, cutting, and finishing between coats",
      "Hand-finish edges, profiles, and detailed joinery components",
      "Inspect finishes for uniformity and correct any imperfections",
      "Apply protective waxes, polishes, and final finishing products",
      "Maintain polishing equipment and abrasives inventory",
    ],
    requirements: [
      "3+ years of experience in furniture or joinery polishing",
      "Experience with high-gloss and matte polishing techniques",
      "Keen eye for detail and surface quality",
      "Patience and precision for fine finishing work",
    ],
  },
  {
    id: "safety-officer",
    title: "Safety Officer",
    department: "Health & Safety",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "3+ Years",
    summary:
      "Oversee workplace safety standards and compliance across our interior fit-out and manufacturing operations, ensuring a safe environment for all site and factory personnel.",
    responsibilities: [
      "Implement and monitor workplace health and safety procedures across fit-out sites and the manufacturing facility",
      "Ensure compliance with Bahrain health and safety regulations and ISO 45001 standards",
      "Conduct regular site safety inspections, risk assessments, and safety audits",
      "Investigate incidents and near-misses, and implement corrective and preventive actions",
      "Deliver safety inductions, toolbox talks, and ongoing training for site and factory teams",
      "Maintain safety documentation, permits to work, and incident reporting records",
    ],
    requirements: [
      "NEBOSH Certification",
      "Civil, Mechanical or Electrical diploma / graduate",
      "3+ years of experience in the same field",
    ],
  },
  {
    id: "finishing-carpenter",
    title: "Finishing Carpenter",
    department: "Joinery & Manufacturing",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "2+ Years",
    summary:
      "Produce high-quality finishing and detailed woodwork for bespoke joinery and furniture, ensuring premium craftsmanship across every project delivered from our Askar manufacturing facility.",
    responsibilities: [
      "Carry out detailed finishing and assembly of bespoke joinery and furniture components",
      "Apply edge banding, veneers, inlays, and intricate wood detailing to specification",
      "Prepare surfaces for spray finishing, including sanding, filling, and priming",
      "Inspect and rectify finishing imperfections to maintain premium quality standards",
      "Work closely with the carpentry and spray teams to ensure seamless handover between stages",
      "Maintain tools, equipment, and workspaces in clean and safe operating condition",
    ],
    requirements: [
      "Minimum 2 years of experience",
      "Expertise in detailed finishing and woodwork craftsmanship",
      "Relevant work experience (minimum 2 years in the respective field)",
      "Commitment to quality and attention to detail",
      "Ability to work as part of a team in a fast-paced environment",
    ],
  },
  {
    id: "furniture-painter",
    title: "Furniture Painter",
    department: "Finishing & Spray",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "2+ Years",
    summary:
      "Apply premium paint and finishing to furniture and joinery in our dust-free spray facility, achieving consistent, flawless results that meet the standards expected by international luxury brands.",
    responsibilities: [
      "Apply spray paint, lacquer, and finishing coats to furniture and joinery components",
      "Prepare surfaces through sanding, priming, filling, and undercoating",
      "Mix and match colours and finishes to project specifications",
      "Operate spray equipment in dust-free booths for smooth, even coverage",
      "Perform quality checks on finish consistency, coverage, and adhesion",
      "Maintain spray equipment, booths, and material inventory",
    ],
    requirements: [
      "Minimum 2 years of experience",
      "Skilled in furniture painting, finishing, and detailing",
      "Relevant work experience (minimum 2 years in the respective field)",
      "Commitment to quality and attention to detail",
      "Ability to work as part of a team in a fast-paced environment",
    ],
  },
  {
    id: "quantity-surveyor",
    title: "Quantity Surveyor",
    department: "Project Management",
    location: "Road 5156, Askar, Kingdom of Bahrain",
    type: "Full-Time",
    experience: "Proven QS Experience",
    summary:
      "Prepare BOQ, cost estimates, valuations, variation orders, and tender documents, working closely with project managers and clients to ensure accurate cost control across interior fit-out projects.",
    responsibilities: [
      "Prepare Bills of Quantities (BOQ), cost estimates, and tender documents",
      "Manage valuations, variation orders, and interim payment certificates",
      "Work closely with project managers and clients on cost control and reporting",
      "Conduct cost analysis, value engineering, and procurement planning",
      "Review subcontractor claims, measurements, and final accounts",
      "Maintain cost databases and ensure compliance with Bahrain market rates and standards",
    ],
    requirements: [
      "Engineering Degree or QS qualification",
      "Proven experience as QS in interiors / construction",
      "Vast knowledge of Bahrain market rates and standards",
      "Proficient in MS Excel, Word, AutoCAD",
    ],
  },
];
